import { Building2, Shield, Lock } from 'lucide-react';
import { SubscriptionModel } from '../../types/configurator';
import { Card, CardContent } from '../ui';

interface SubscriptionModelToggleProps {
  subscriptionModel: SubscriptionModel;
  onUpdate: (enabled: boolean, providerName?: string, customerName?: string) => void;
}

export function SubscriptionModelToggle({
  subscriptionModel,
  onUpdate,
}: SubscriptionModelToggleProps) {
  return (
    <Card className="bg-gradient-to-r from-orange-50 to-blue-50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-gray-600" />
            <div>
              <h3 className="font-medium text-gray-900">Managed Deployment Model</h3>
              <p className="text-sm text-gray-500">
                Enable provider/customer network separation for subscription deployments
              </p>
            </div>
          </div>
          <label className="relative inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              checked={subscriptionModel.enabled}
              onChange={(e) =>
                onUpdate(
                  e.target.checked,
                  subscriptionModel.providerName,
                  subscriptionModel.customerName
                )
              }
              className="peer sr-only"
            />
            <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-hermit-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-2 peer-focus:ring-hermit-300" />
          </label>
        </div>

        {subscriptionModel.enabled && (
          <div className="mt-4 border-t border-orange-200/50 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">
                  Provider Name
                </label>
                <input
                  type="text"
                  value={subscriptionModel.providerName || ''}
                  onChange={(e) =>
                    onUpdate(true, e.target.value, subscriptionModel.customerName)
                  }
                  placeholder="e.g., hermit-μDC Services"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-hermit-500 focus:outline-none focus:ring-1 focus:ring-hermit-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">
                  Customer Name
                </label>
                <input
                  type="text"
                  value={subscriptionModel.customerName || ''}
                  onChange={(e) =>
                    onUpdate(true, subscriptionModel.providerName, e.target.value)
                  }
                  placeholder="e.g., Acme Corp"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-hermit-500 focus:outline-none focus:ring-1 focus:ring-hermit-500"
                />
              </div>
            </div>

            {/* Access Legend */}
            <div className="mt-4 flex gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-orange-500" />
                <span className="text-gray-600">Provider Access:</span>
                <span className="text-gray-500">Admin, Monitoring</span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-blue-500" />
                <span className="text-gray-600">Customer Only:</span>
                <span className="text-gray-500">User Data</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
